import { PersonAppearance, PersonLane } from './types';

/** An appearance assigned to a sub-row inside the summary strip. */
export type PackedAppearance = {
  lane: PersonLane;
  appearance: PersonAppearance;
  row: number;
};

/**
 * Packs all appearances from `lanes` into at most `maxRows` sub-rows.
 * Greedy: place each item (sorted by `from`) in the first row whose previous end
 * plus `toleranceMs` is before this start; open a new row if under the cap;
 * otherwise put it on the row with the earliest end (accepting visual overlap).
 * Never drops an appearance.
 *
 * Callers that pack once for a loaded stream range should pass a fixed
 * tolerance (e.g. STREAM_UI_CONFIG.personsPackingOverlapToleranceMs), not a
 * focus-zoom-derived value, so row assignments stay stable while scrubbing.
 */
export function packAppearancesIntoRows(
  lanes: PersonLane[],
  maxRows: number,
  toleranceMs: number,
): PackedAppearance[] {
  const items: { lane: PersonLane; appearance: PersonAppearance }[] = [];
  for (const lane of lanes) {
    for (const appearance of lane.face) {
      items.push({ lane, appearance });
    }
    for (const appearance of lane.voice) {
      items.push({ lane, appearance });
    }
  }

  items.sort((a, b) => {
    const diff = a.appearance.from - b.appearance.from;
    if (diff !== 0) {
      return diff;
    }
    return a.lane.laneKey.localeCompare(b.lane.laneKey);
  });

  const rowEnds: number[] = [];
  const result: PackedAppearance[] = [];

  for (const item of items) {
    let assigned = -1;
    for (let r = 0; r < rowEnds.length; r++) {
      if (rowEnds[r] + toleranceMs <= item.appearance.from) {
        assigned = r;
        break;
      }
    }

    if (assigned === -1) {
      if (rowEnds.length < maxRows) {
        assigned = rowEnds.length;
        rowEnds.push(0);
      } else {
        assigned = 0;
        for (let r = 1; r < rowEnds.length; r++) {
          if (rowEnds[r] < rowEnds[assigned]) {
            assigned = r;
          }
        }
      }
    }

    rowEnds[assigned] = item.appearance.to;
    result.push({ ...item, row: assigned });
  }

  return result;
}
